import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '@/store';
import { excludeCall } from '@/store/slices/callReviewSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';

interface Props {
  visible: boolean;
  callUuid: string | null;
  onClose: () => void;
  onExcluded: () => void;
}

/**
 * Centered modal to exclude a call from stats with a required note (admin). Uses
 * an in-app TextInput rather than Alert.prompt (iOS-only) / Alert.alert (a no-op
 * on react-native-web). Dispatches excludeCall; the slice removes the call from
 * the unassigned list on success.
 */
const ExcludeCallModal: React.FC<Props> = ({ visible, callUuid, onClose, onExcluded }) => {
  const dispatch = useDispatch<AppDispatch>();
  const excludingUuid = useSelector((s: RootState) => s.callReview.excludingUuid);
  const busy = excludingUuid != null;

  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setReason('');
      setError(null);
    }
  }, [visible]);

  const confirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Please enter a reason.');
      return;
    }
    if (!callUuid) {
      return;
    }
    setError(null);
    const result = await dispatch(excludeCall({ callUuid, reason: trimmed }));
    if (excludeCall.fulfilled.match(result)) {
      onExcluded();
      onClose();
    } else {
      setError((result.payload as string) || 'Could not exclude the call.');
    }
  };

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>Exclude from Stats</Text>
          <Text style={styles.subtitle}>
            This removes the call from all call-quality stats. Add a short reason.
          </Text>
          <TextInput
            style={styles.input}
            value={reason}
            onChangeText={setReason}
            placeholder="Reason (e.g. wrong number, test call)…"
            placeholderTextColor={colors.inkFaint}
            multiline
            numberOfLines={3}
            autoFocus
          />
          {!!error && <Text style={styles.error}>{error}</Text>}
          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={busy}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmBtn, busy && styles.confirmBtnBusy]}
              onPress={confirm}
              disabled={busy}>
              {busy ? (
                <ActivityIndicator color={colors.onAccent} />
              ) : (
                <Text style={styles.confirmText}>Exclude</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'center',
    padding: space.xl,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
  },
  title: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
  },
  subtitle: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.xs,
    marginBottom: space.md,
  },
  input: {
    backgroundColor: colors.surfaceSunk,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    padding: space.md,
    minHeight: 80,
    textAlignVertical: 'top',
    fontFamily: font.body,
    fontSize: 15,
    color: colors.ink,
  },
  error: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
    marginTop: space.sm,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: space.md,
    marginTop: space.lg,
  },
  cancelBtn: {
    paddingHorizontal: space.lg,
    paddingVertical: space.sm,
  },
  cancelText: {
    fontFamily: font.bodySemi,
    fontSize: 15,
    color: colors.inkSoft,
  },
  confirmBtn: {
    backgroundColor: colors.accent,
    borderRadius: radius.pill,
    paddingHorizontal: space.xl,
    paddingVertical: space.sm,
    minWidth: 96,
    alignItems: 'center',
  },
  confirmBtnBusy: {
    opacity: 0.7,
  },
  confirmText: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.onAccent,
  },
});

export default ExcludeCallModal;
